import React, { memo, useState } from 'react';
import {
  EdgeProps,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
  Edge,
} from '@xyflow/react';
import { ConnectorData, ConnectorType } from '../types/diagram.types';
import { CONNECTOR_STYLES } from '../constants/connectorStyles';

type ConnectorEdgeType = Edge<ConnectorData, 'connector'>;

const ConnectorEdge = memo((props: EdgeProps<ConnectorEdgeType>) => {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
    selected,
    style: externalStyle,
    markerEnd,
  } = props;

  const [hovered, setHovered] = useState(false);

  const connType = data?.type ?? 'REST';
  const cs = CONNECTOR_STYLES[connType as ConnectorType] ?? CONNECTOR_STYLES.REST;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const strokeColor = cs.color;
  const strokeWidth = selected || hovered ? cs.strokeWidth + 1 : cs.strokeWidth;
  const strokeDash = cs.strokeDasharray ?? undefined;

  const edgeStyle: React.CSSProperties = {
    stroke: strokeColor,
    strokeWidth,
    strokeDasharray: strokeDash,
    filter: selected || hovered ? `drop-shadow(0 0 4px ${strokeColor}88)` : undefined,
    ...externalStyle,
  };

  const showTooltip = hovered && !!data;

  return (
    <>
      {/* Invisible wider path for easier hover */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={12}
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />
      <BaseEdge
        id={id}
        path={edgePath}
        style={edgeStyle}
        markerEnd={markerEnd ?? `url(#arrow-${connType})`}
      />
      {/* Edge label */}
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
            cursor: 'pointer',
            zIndex: hovered ? 100 : 1,
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {/* Type badge */}
          <div
            style={{
              background: strokeColor,
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
              letterSpacing: '0.04em',
              opacity: 0.9,
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            {data?.index != null && (
              <span style={{ opacity: 0.75, fontSize: 8, fontWeight: 600 }}>#{data.index}</span>
            )}
            {cs.label}
            {data?.method && ` ${data.method}`}
          </div>

          {/* Tooltip popup on hover */}
          {showTooltip && (
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: 6,
                background: '#212121',
                color: '#fff',
                borderRadius: 6,
                padding: '8px 10px',
                fontSize: 11,
                minWidth: 180,
                maxWidth: 260,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                zIndex: 200,
                lineHeight: 1.5,
                whiteSpace: 'normal',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4, color: strokeColor }}>
                {data?.index != null ? `#${data.index} · ` : ''}{connType}
              </div>
              {data?.label && (
                <div style={{ marginBottom: 3, wordBreak: 'break-all' }}>{data.label}</div>
              )}
              {data?.eventName && (
                <div style={{ color: '#BDBDBD' }}>Event: {data.eventName}</div>
              )}
              {data?.slaMs && (
                <div style={{ color: '#BDBDBD' }}>SLA: {data.slaMs}ms</div>
              )}
              {data?.auth && (
                <div style={{ color: '#BDBDBD' }}>Auth: {data.auth}</div>
              )}
              {data?.isAsync && (
                <div style={{ color: '#FFB74D', marginTop: 2 }}>⚡ Async</div>
              )}
              {data?.flowIds && data.flowIds.length > 0 && (
                <div style={{ color: '#81C784', marginTop: 4, fontSize: 10 }}>
                  Flows: {data.flowIds.length}
                </div>
              )}
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
});

ConnectorEdge.displayName = 'ConnectorEdge';
export default ConnectorEdge;
